/**
 * The header states for the folded Settings sections (#456 player, #459
 * coach).
 *
 * WHY A LIB RATHER THAN FIVE INLINE EXPRESSIONS. `SectionCard`'s rule is
 * that a collapsible card carries exactly one of `count` and `summary`, and
 * that the summary must say what is inside the fold — "a fold that does not
 * tell you what is inside is worse than no fold at all" (#378, restated for
 * settings sections in `components/identityForm.jsx`). A summary derived
 * inline in a render body is a rule with no behavioural test: this repo
 * mounts no components in unit tests, because `components/ui/*` imports
 * through the `@/...` alias that webpack and jsconfig declare and jest does
 * not (see `ConditionUpdate.test.js`). Out here each derivation is a plain
 * function over plain data, and `settingsSummary.test.js` tests it for real
 * — the same split `lib/sectionCardState.js` uses for the invariant itself.
 *
 * EVERY SUMMARY IS DERIVED FROM LIVE STATE. None of these returns a fixed
 * string for a section that has state, because the point of the badge is to
 * answer "do I need to open this?" without opening it. The one section
 * that genuinely has no state to report (Send feedback)
 * gets `SUMMARY_NONE` — an explicit nothing, the settings analogue of
 * `count={0}`, not a silent absence.
 */
import { HEALTH_FIELDS } from "./playerHealth";

/**
 * The em dash a section with genuinely nothing to report passes. Named
 * rather than typed out at three call sites so "the explicit nothing" is
 * one decision with one spelling — an em dash and a hyphen look alike in a
 * diff and read differently on a phone.
 */
export const SUMMARY_NONE = "—";

/**
 * The three text sizes, and the `--nw-base-font` value each one sets.
 *
 * Moved here from `pages/Settings.jsx` with the card that owns it (#456).
 * NOT shared with `components/AccessibilityMenu.jsx`'s near-identical list:
 * that one labels the same three keys "Aa" at three sizes with the words as
 * the description, because it is a 3-across popover of type samples rather
 * than a row of labelled option cards. Same keys, same pixels, deliberately
 * different words — merging them would force one surface to say the other's
 * thing. `public/index.html` re-states the pixel values a third time on
 * purpose, so the choice applies before any JS bundle has parsed.
 */
export const TEXT_SIZES = [
  { key: "compact", label: "Compact", px: "15px", desc: "Denser layout" },
  { key: "default", label: "Default", px: "17px", desc: "Recommended" },
  { key: "large", label: "Large", px: "19px", desc: "Easier to read" },
];

/** The stored option for a key, falling back to Default the same way
 * `applyTextSize` does — an unknown key in localStorage must not blank the
 * badge, it must read as what the page is actually rendering at. */
export function textSizeOption(key) {
  return TEXT_SIZES.find((t) => t.key === key) || TEXT_SIZES[1];
}

/**
 * Text size's header state: the size that is actually in force, e.g.
 * "Large" — the shape Andreas approved for this card.
 *
 * High contrast is APPENDED when it is on, and that is not decoration. The
 * card folds two settings, not one, and a badge that reported only the size
 * would hide the second one behind exactly the fold #378 forbids. When it
 * is off the badge is the bare size label, which is the approved shape and
 * the overwhelmingly common case.
 *
 * NOT MADE FLAG-AWARE FOR #797. The card this badge summarises lives on
 * Settings, which is itself a shipped screen the #797 retirement never
 * touches — `nw_text_size` is exactly as true there, on every text size a
 * player has ever been able to pick, before and after #797. A flag-aware
 * caveat ("…but this does nothing on your set-up screen") would describe a
 * DIFFERENT page than the one this badge sits on, which is not this
 * badge's job — `SUMMARY_NONE`'s own contract (this file's top comment) is
 * "say what is inside THIS fold", not survey every other screen in the
 * app. That caveat belongs one level up, in the card's own `desc` prose
 * (`TextSizeCard.jsx`), which is exactly where it was added — the same
 * split `healthSummary` already draws between a terse badge and its
 * card's fuller prose.
 */
export function textSizeSummary(key, highContrast) {
  const label = textSizeOption(key).label;
  return highContrast ? `${label} · High contrast` : label;
}

/**
 * Notifications' header state: how many channels are switched on, e.g.
 * "3 on". Reads the flags in whatever order the caller lists them and
 * counts the truthy ones, so adding a fourth channel needs no change here.
 *
 * "0 on" is a real and useful answer — the analogue of `count={0}` — so
 * there is no empty-string branch.
 */
export function notificationsSummary(flags) {
  const on = flags.filter(Boolean).length;
  return `${on} on`;
}

/**
 * "What we measure"'s header state (#1109): "On" while the person's use of
 * the app is counted, "Off" once they have switched it off. Takes the
 * server's `opted_out` flag. `null`/`undefined` (still loading, or the read
 * failed) is `SUMMARY_NONE`: the header never guesses a state it has not
 * been told.
 */
export function usageSummary(optedOut) {
  if (typeof optedOut !== "boolean") return SUMMARY_NONE;
  return optedOut ? "Off" : "On";
}

const filled = (v) => typeof v === "string" && v.trim() !== "";

/**
 * Health and emergency contact's header state.
 *
 * DERIVED FROM THE SAVED RECORD, not the form: the badge answers "what do
 * my coaches see", and an unsaved keystroke has not reached them.
 *
 * "Incomplete" is deliberately NOT one of the words, even though it was the
 * shape sketched when this was approved. Nothing on that card is required —
 * the component says so at length, and the empty state has to stay
 * meaningfully empty for the coach-facing "Not provided" to mean anything.
 * "Incomplete" would assert an obligation the product does not have and
 * would nag a player who has nothing to declare. "Partly filled" reports
 * the same fact and claims nothing.
 *
 * The load failure gets its own word rather than falling through to "Not
 * provided", for the reason the card refuses to render an empty form on
 * failure (#271): "we could not fetch this" and "you told us nothing" are
 * opposite facts, and confusing them here would invite a player to retype a
 * record we merely failed to read.
 */
export function healthSummary({ failed = false, record = null } = {}) {
  if (failed) return "Unavailable";
  if (!record) return "…";
  const n = HEALTH_FIELDS.filter((k) => filled(record[k])).length;
  if (n === 0) return "Not provided";
  return n === HEALTH_FIELDS.length ? "Complete" : "Partly filled";
}

/**
 * The Player Profile fold's header state (#1135, R3): the club and team the
 * player wrote, joined with " · ", or "Not provided" when both are empty. It
 * reads the values the profile form holds, so it changes as they are saved.
 */
export function cricketProfileSummary({ club, team } = {}) {
  const parts = [club, team].filter(filled).map((v) => v.trim());
  return parts.length ? parts.join(" · ") : "Not provided";
}
